import api from "../api/axiosInstance";

const sailingService = {
    getAll: (params) => api.get("/sailing", { params }),
    getOne: (id) => api.get(`/sailing/${id}`),
    create: (data) => api.post("/sailing", data),
    update: (id, data) => api.patch(`/sailing/${id}`, data),
    getAssignable: () => api.get("/sailing/assignable"),
    assignShipments: (id, shipmentIds) => api.post(`/sailing/${id}/assign`, { shipmentIds }),
    unassignShipment: (id, shipmentId) => api.delete(`/sailing/${id}/unassign/${shipmentId}`),
    // Removes every item currently assigned to this sailing at once —
    // each resets to "received", same as unassigning one at a time.
    unassignAllItems: (id) => api.delete(`/sailing/${id}/unassign-all`),
    getManifest: (id) => api.get(`/sailing/${id}/manifest`),
    getManifestXlsx: (id) => api.get(`/sailing/${id}/manifest.xlsx`, { responseType: "blob" }),
    getManifestPdf: (id) => api.get(`/sailing/${id}/manifest.pdf`, { responseType: "blob" }),
    getFreightAllocation: (id) => api.get(`/sailing/${id}/freight-allocation`),
    updateFreightAllocation: (id, allocations) =>
        api.patch(`/sailing/${id}/freight-allocation`, { allocations }),
    // `tin` selects which of a multi-TIN customer's House Bills — omit/pass
    // "" for a single-TIN customer, unchanged from before.
    getHouseBol: (id, customerId, tin = "") =>
        api.get(`/sailing/${id}/house-bol/${customerId}`, { params: tin ? { tin } : undefined }),
    downloadHouseBol: (id, customerId, tin = "") =>
        api.get(`/sailing/${id}/house-bol/${customerId}/download`, {
            params: tin ? { tin } : undefined,
            responseType: "blob",
        }),
    getHouseBolPdf: (id, customerId, tin = "") =>
        api.get(`/sailing/${id}/house-bol/${customerId}/pdf`, {
            params: tin ? { tin } : undefined,
            responseType: "blob",
        }),
    // Every House Bill that exists across every sailing, grouped by
    // sailing, for the House BOL tab's list. Paginated at the sailing level
    // — page is 0-indexed, matching MUI's TablePagination.
    getAllHouseBols: (page = 0, limit = 10) => api.get("/sailing/house-bols", { params: { page, limit } }),
    // Replaces the whole fees list for one customer's House Bill on a
    // sailing — fees is [{ title, amount }].
    setHouseBolFees: (id, customerId, tin, fees) =>
        api.patch(`/sailing/${id}/house-bol/${customerId}/fees`, { tin: tin || "", fees }),
    // Admin picks which of a multi-TIN customer's TINs a blank-TIN HBOL was
    // actually filed under.
    setHouseBolTin: (id, customerId, tin) =>
        api.patch(`/sailing/${id}/house-bol/${customerId}/tin`, { tin }),
    // Every sailing in the DB, with whatever MBL fees have been recorded —
    // for the Finance > MBL Fees page. Paginated, 0-indexed.
    getAllSailingsForMblFees: (page = 0, limit = 10) => api.get("/sailing/mbl-fees", { params: { page, limit } }),
    // Replaces the whole MBL fees list for one sailing — fees is
    // [{ title, amount }].
    setSailingMblFees: (id, fees) => api.patch(`/sailing/${id}/mbl-fees`, { fees }),
    // Surgically sets (or clears, if amount is blank) ONE named mblFees
    // line — leaves every other line untouched. Used by the HBOL Fees
    // page's per-sailing "Duties (MBL)" field.
    setSailingMblFeeLine: (id, title, amount) => api.patch(`/sailing/${id}/mbl-fee-line`, { title, amount }),
    // Every sailing with a D/R on its roster, with whatever Port Fees have
    // been recorded — for the Finance > Port Fees page. Paginated, 0-indexed.
    getAllSailingsForPortFees: (page = 0, limit = 10) => api.get("/sailing/port-fees", { params: { page, limit } }),
    // Replaces the whole Port Fees list for one sailing — fees is
    // [{ dockReceiptNumber, cargoCharge, truckingCharge, invoiceNumber, invoiceDate }].
    setSailingPortFees: (id, fees) => api.patch(`/sailing/${id}/port-fees`, { fees }),
    // Replaces the whole AD-HOC Port Fees list for one sailing (not tied to
    // a D/R) — fees is [{ title, amount }], same "Add Fee" shape as MBL Fees.
    setSailingPortFeesExtra: (id, fees) => api.patch(`/sailing/${id}/port-fees-extra`, { fees }),
    getInvoicesZip: (id) => api.get(`/sailing/${id}/invoices.zip`, { responseType: "blob" }),
    // Manifest + MBL (if one's been uploaded) + every customer's HBOL +
    // every customer's invoice, all in one zip — the "download everything
    // for this sailing" button on the Sailing list/detail pages.
    getDocumentsZip: (id) => api.get(`/sailing/${id}/documents.zip`, { responseType: "blob" }),
    // Returns { url } — a short-lived presigned S3 URL, not a blob (mirrors
    // documentService.download's pattern).
    downloadBol: (id) => api.get(`/sailing/${id}/bol/download`),
    // Archives a BOL PDF against this sailing only — no parsing, no D/R
    // matching. Distinct from the sidebar's "Upload BOL" (/sailing/bol/parse
    // + /bol/confirm), which extracts and assigns data from the BOL.
    uploadBol: (id, file) => {
        const formData = new FormData();
        formData.append("file", file);
        return api.post(`/sailing/${id}/bol/upload`, formData, {
            headers: { "Content-Type": "multipart/form-data" },
        });
    },
};

export default sailingService;